import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Settings, Percent, Save, Loader2, RefreshCw, DollarSign, Truck, ShoppingCart, CreditCard } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface SettingValue {
  rate?: number;
  amount?: number;
  max_fee?: number;
  enabled: boolean;
}

interface PlatformSetting {
  key: string;
  value: SettingValue;
  description: string;
}

export default function PlatformSettings() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  
  // Settings state
  const [settings, setSettings] = useState<Record<string, SettingValue>>({
    vat_rate: { rate: 5, enabled: true },
    commission_rate: { rate: 5, enabled: true },
    minimum_order_amount: { amount: 1000, enabled: true },
    delivery_fee_cap: { max_fee: 5000, enabled: false },
    payment_gateway_fee: { rate: 1.5, enabled: true }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('key, value, description');

      if (error) throw error;

      if (data) {
        const settingsMap: Record<string, SettingValue> = {};
        data.forEach((setting) => {
          settingsMap[setting.key] = setting.value as unknown as SettingValue;
        });
        setSettings(prev => ({ ...prev, ...settingsMap }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Error",
        description: "Failed to load platform settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSetting = async (key: string, value: SettingValue) => {
    setSaving(key);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('platform_settings')
        .update({
          value: value as any,
          updated_by: user?.id
        })
        .eq('key', key);

      if (error) throw error;

      toast({
        title: "Setting Saved",
        description: `${key.replace(/_/g, ' ')} has been updated`,
      });
    } catch (error) {
      console.error('Error saving setting:', error);
      toast({
        title: "Error",
        description: "Failed to save setting",
        variant: "destructive"
      });
    } finally {
      setSaving(null);
    }
  };

  const updateSetting = (key: string, updates: Partial<SettingValue>) => {
    setSettings(prev => ({
      ...prev,
      [key]: { ...prev[key], ...updates }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Platform Settings
          </h2>
          <p className="text-muted-foreground">Configure global platform settings</p>
        </div>
        <Button variant="outline" onClick={fetchSettings} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* VAT Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5" />
              VAT Configuration
            </CardTitle>
            <CardDescription>
              Value Added Tax applied to all products
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable VAT</Label>
                <p className="text-xs text-muted-foreground">Apply VAT to transactions</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings.vat_rate?.enabled}
                  onCheckedChange={(enabled) => updateSetting('vat_rate', { enabled })}
                />
                <Badge variant={settings.vat_rate?.enabled ? "default" : "secondary"}>
                  {settings.vat_rate?.enabled ? "On" : "Off"}
                </Badge>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>VAT Rate (%)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.vat_rate?.rate || 0}
                  onChange={(e) => updateSetting('vat_rate', { rate: parseFloat(e.target.value) || 0 })}
                  className="w-24"
                  disabled={!settings.vat_rate?.enabled}
                />
                <span className="text-muted-foreground">%</span>
              </div>
            </div>

            <Button 
              onClick={() => saveSetting('vat_rate', settings.vat_rate)} 
              disabled={saving === 'vat_rate'} 
              className="w-full"
              size="sm"
            >
              {saving === 'vat_rate' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save VAT Settings
            </Button>
          </CardContent>
        </Card>

        {/* Commission Rate */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Commission Rate
            </CardTitle>
            <CardDescription>
              Platform commission on each transaction
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Commission</Label>
                <p className="text-xs text-muted-foreground">Charge commission on sales</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings.commission_rate?.enabled}
                  onCheckedChange={(enabled) => updateSetting('commission_rate', { enabled })}
                />
                <Badge variant={settings.commission_rate?.enabled ? "default" : "secondary"}>
                  {settings.commission_rate?.enabled ? "On" : "Off"}
                </Badge>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Commission Rate (%)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.commission_rate?.rate || 0}
                  onChange={(e) => updateSetting('commission_rate', { rate: parseFloat(e.target.value) || 0 })}
                  className="w-24"
                  disabled={!settings.commission_rate?.enabled}
                />
                <span className="text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Sellers receive {100 - (settings.commission_rate?.rate || 0)}% of each sale
              </p>
            </div>

            <Button 
              onClick={() => saveSetting('commission_rate', settings.commission_rate)} 
              disabled={saving === 'commission_rate'} 
              className="w-full"
              size="sm"
            >
              {saving === 'commission_rate' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Commission Settings
            </Button>
          </CardContent>
        </Card>

        {/* Minimum Order Amount */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Minimum Order Amount
            </CardTitle>
            <CardDescription>
              Minimum order value required for checkout
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Minimum Order</Label>
                <p className="text-xs text-muted-foreground">Require minimum cart value</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings.minimum_order_amount?.enabled}
                  onCheckedChange={(enabled) => updateSetting('minimum_order_amount', { enabled })}
                />
                <Badge variant={settings.minimum_order_amount?.enabled ? "default" : "secondary"}>
                  {settings.minimum_order_amount?.enabled ? "On" : "Off"}
                </Badge>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Minimum Amount (₦)</Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">₦</span>
                <Input
                  type="number"
                  min="0"
                  step="100"
                  value={settings.minimum_order_amount?.amount || 0}
                  onChange={(e) => updateSetting('minimum_order_amount', { amount: parseFloat(e.target.value) || 0 })}
                  className="w-32"
                  disabled={!settings.minimum_order_amount?.enabled}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Orders below ₦{(settings.minimum_order_amount?.amount || 0).toLocaleString()} will be blocked
              </p>
            </div>

            <Button 
              onClick={() => saveSetting('minimum_order_amount', settings.minimum_order_amount)} 
              disabled={saving === 'minimum_order_amount'} 
              className="w-full"
              size="sm"
            >
              {saving === 'minimum_order_amount' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Minimum Order Settings
            </Button>
          </CardContent>
        </Card>

        {/* Delivery Fee Cap */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Delivery Fee Cap
            </CardTitle>
            <CardDescription>
              Maximum delivery fee for all orders
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Fee Cap</Label>
                <p className="text-xs text-muted-foreground">Limit maximum delivery fees</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings.delivery_fee_cap?.enabled}
                  onCheckedChange={(enabled) => updateSetting('delivery_fee_cap', { enabled })}
                />
                <Badge variant={settings.delivery_fee_cap?.enabled ? "default" : "secondary"}>
                  {settings.delivery_fee_cap?.enabled ? "On" : "Off"}
                </Badge>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Maximum Fee (₦)</Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">₦</span>
                <Input
                  type="number"
                  min="0"
                  step="100"
                  value={settings.delivery_fee_cap?.max_fee || 0}
                  onChange={(e) => updateSetting('delivery_fee_cap', { max_fee: parseFloat(e.target.value) || 0 })}
                  className="w-32"
                  disabled={!settings.delivery_fee_cap?.enabled}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Delivery fees will not exceed ₦{(settings.delivery_fee_cap?.max_fee || 0).toLocaleString()}
              </p>
            </div>

            <Button 
              onClick={() => saveSetting('delivery_fee_cap', settings.delivery_fee_cap)} 
              disabled={saving === 'delivery_fee_cap'} 
              className="w-full"
              size="sm"
            >
              {saving === 'delivery_fee_cap' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Delivery Cap Settings
            </Button>
          </CardContent>
        </Card>

        {/* Payment Gateway Fee */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Gateway Fee
            </CardTitle>
            <CardDescription>
              Additional payment processing fee (typically charged by Paystack)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Gateway Fee</Label>
                <p className="text-xs text-muted-foreground">Pass gateway fees to customers</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings.payment_gateway_fee?.enabled}
                  onCheckedChange={(enabled) => updateSetting('payment_gateway_fee', { enabled })}
                />
                <Badge variant={settings.payment_gateway_fee?.enabled ? "default" : "secondary"}>
                  {settings.payment_gateway_fee?.enabled ? "On" : "Off"}
                </Badge>
              </div>
            </div>

            <Separator />

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gateway Fee Rate (%)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    value={settings.payment_gateway_fee?.rate || 0}
                    onChange={(e) => updateSetting('payment_gateway_fee', { rate: parseFloat(e.target.value) || 0 })}
                    className="w-24"
                    disabled={!settings.payment_gateway_fee?.enabled}
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">Fee Calculation Example</h4>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Order Total: ₦10,000</p>
                  <p>Gateway Fee ({settings.payment_gateway_fee?.rate || 0}%): ₦{settings.payment_gateway_fee?.enabled ? ((10000 * (settings.payment_gateway_fee?.rate || 0)) / 100).toLocaleString() : '0'}</p>
                  <p className="font-medium text-foreground">
                    Customer Pays: ₦{settings.payment_gateway_fee?.enabled ? (10000 + (10000 * (settings.payment_gateway_fee?.rate || 0)) / 100).toLocaleString() : '10,000'}
                  </p>
                </div>
              </div>
            </div>

            <Button 
              onClick={() => saveSetting('payment_gateway_fee', settings.payment_gateway_fee)} 
              disabled={saving === 'payment_gateway_fee'} 
              className="w-full"
              size="sm"
            >
              {saving === 'payment_gateway_fee' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Gateway Fee Settings
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
